import { useContext, useEffect } from 'react';
import { Button, Tooltip, Dropdown } from 'antd';
import { CheckOutlined, UpOutlined, VideoCameraAddOutlined, VideoCameraOutlined } from '@ant-design/icons';
import ZoomMediaContext from '../../../context/media-context';
import classNames from 'classnames';
import type { MediaDevice } from '../video-types';
import { type MenuItem, getAntdDropdownMenu, getAntdItem } from './video-footer-utils';
import { ProcessorName } from '../../../App';
import { ProcessorParams } from '@zoom/videosdk';
import { IconFont } from '../../../component/icon-font';
interface CameraButtonProps {
  isStartedVideo: boolean;
  isMirrored?: boolean;
  isBlur?: boolean;
  isPreview?: boolean;
  isCameraForbidden?: boolean;
  onCameraClick: () => void;
  onSwitchCamera: (deviceId: string) => void;
  onMirrorVideo?: () => void;
  onVideoStatistic?: () => void;
  onBlurBackground?: () => void;
  onSelectVideoPlayback?: (url: string) => void;
  onSelectVideoProcessor?: (processor: any) => void;
  className?: string;
  cameraList?: MediaDevice[];
  activeCamera?: string;
  activePlaybackUrl?: string;
  activeProcessor?: string;
}
const videoPlaybacks = [
  { title: 'ZOOM ZWA', url: 'https://source.zoom.us/brand/mp4/Using%20the%20Zoom%20PWA.mp4' },
  { title: 'ZOOM Cares', url: 'https://source.zoom.us/brand/mp4/Zoom%20Cares%20Nonprofit%20Impact.mp4' },
  {
    title: 'ZOOM One',
    url: 'https://source.zoom.us/brand/mp4/Zoom%20One%20-%20Team%20Chat%2C%20Phone%2C%20Email%2C%20and%20more.mp4'
  }
];
export const videoProcessor: ProcessorParams[] = [
  // {
  //   url: `${location.origin}/static/processors/watermark-processor.js`,
  //   type: 'video',
  //   name: 'watermark-processor',
  //   options: {}
  // },
  {
    url: `${location.origin}/static/processors/pose-processor.js`,
    type: 'video',
    name: 'pose-processor',
    options: {}
  }
];

const CameraIcon = ({ off }: { off: boolean }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    {off && <line x1="2" y1="2" x2="22" y2="22" strokeWidth="2.5" />}
  </svg>
);
const ChevronUp = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const CameraButton = (props: CameraButtonProps) => {
  const {
    isStartedVideo,
    className,
    cameraList,
    activeCamera,
    isMirrored,
    isBlur,
    isPreview,
    isCameraForbidden,
    activePlaybackUrl,
    onCameraClick,
    onSwitchCamera,
    onMirrorVideo,
    onVideoStatistic,
    onBlurBackground,
    onSelectVideoPlayback,
    onSelectVideoProcessor,
    activeProcessor
  } = props;
  const { mediaStream } = useContext(ZoomMediaContext);

  const onMenuItemClick = (payload: { key: any }) => {
    const processor = videoProcessor.find((item) => item.name === payload.key);
    if (payload.key === 'mirror') {
      onMirrorVideo?.();
    } else if (payload.key === 'statistic') {
      onVideoStatistic?.();
    } else if (payload.key === 'blur') {
      onBlurBackground?.();
    } else if (/^https:\/\//.test(payload.key)) {
      onSelectVideoPlayback?.(payload.key);
    } else if (processor) {
      onSelectVideoProcessor?.(processor);
    } else {
      onSwitchCamera(payload.key);
    }
  };
  const menuItems =
    cameraList &&
    cameraList.length > 0 &&
    ([
      getAntdItem(
        'Select a Camera',
        'camera',
        undefined,
        cameraList.map((item) =>
          getAntdItem(item.label, item.deviceId, item.deviceId === activeCamera && <CheckOutlined />)
        ),
        'group'
      ),
      // !isPreview &&
      //   getAntdItem(
      //     'Select a Video Playback',
      //     'playback',
      //     undefined,
      //     videoPlaybacks.map((item) =>
      //       getAntdItem(item.title, item.url, item.url === activePlaybackUrl && <CheckOutlined />)
      //     ),
      //     'group'
      //   ),

      !isPreview &&
        mediaStream?.isSupportVideoProcessor() &&
        getAntdItem(
          'Select a Video Processor',
          'processor',
          undefined,
          videoProcessor.map((item) =>
            getAntdItem(item.name, item.name, item.name === activeProcessor && <CheckOutlined />)
          ),
          'group'
        ),
      getAntdItem('', 'd1', undefined, undefined, 'divider'),
      !isPreview && getAntdItem('Mirror My Video', 'mirror', isMirrored && <CheckOutlined />),
      // mediaStream?.isSupportVirtualBackground()
      //   ? getAntdItem('Blur My Background', 'blur', isBlur && <CheckOutlined />)
      //   : getAntdItem('Mask My Background', 'blur'),
      !isPreview && getAntdItem('', 'd2', undefined, undefined, 'divider'),
      !isPreview && getAntdItem('Video Statistic', 'statistic')
    ].filter(Boolean) as MenuItem[]);
  return (
    <div className={classNames('camera-footer', className)}>
      {isStartedVideo && menuItems ? (
        <Dropdown.Button
          className="vc-dropdown-button"
          style={{ marginRight: 0, paddingRight: 10 }}
          size="large"
          menu={getAntdDropdownMenu(menuItems, onMenuItemClick)}
          onClick={onCameraClick}
          trigger={['click']}
          type="ghost"
          icon={<UpOutlined />}
          placement="topRight"
        >
          <div style={styles.pillBtn} className="press-effect-btn" title="Stop camera">
            <CameraIcon off={false} />
          </div>
        </Dropdown.Button>
      ) : (
        <Tooltip title={`${isStartedVideo ? 'stop camera' : 'start camera'}`}>
          <div style={styles.iconGroup}>
            {isStartedVideo ? (
              <button onClick={onCameraClick} style={styles.pillBtn} className="press-effect-btn" title="Stop camera">
                <CameraIcon off={false} />
              </button>
            ) : (
              <button
                onClick={onCameraClick}
                style={{ ...styles.circleBtn, ...styles.redBtn }}
                className="press-effect-btn"
                title="Start camera"
              >
                <CameraIcon off={true} />
              </button>
            )}
          </div>
        </Tooltip>
      )}
    </div>
  );
};
export default CameraButton;

const styles = {
  iconGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  circleBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    flexShrink: 0,
    border: 'none'
  },
  pillBtn: {
    height: '44px',
    width: '44px',
    // padding: '0 18px',
    borderRadius: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    background: '#f0f0f0',
    color: '#1a1a1a',
    transition: 'all 0.2s ease',
    flexShrink: 0,
    border: 'none'
  },
  redBtn: {
    background: '#C0392B',
    color: '#fff',
    boxShadow: '0 2px 8px rgba(192,57,43,0.4)'
  },
  chevronBtn: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.5)',
    padding: '4px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 0.2s',
    flexShrink: 0
  }
};
